import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Grid,
    Card,
    CardContent,
    Box,
    Typography,
    Chip,
    Skeleton
} from '@mui/material';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { transformToBTC } from '../../../../utils/helpers';

const UserVolumeOverview = ({ userData, loading }) => {
    const { t } = useTranslation();

    const typeDistribution = [
        { name: t('user.deposits'), value: userData?.totalDeposits || 0, color: '#4caf50' },
        { name: t('user.withdrawals'), value: userData?.totalWithdrawals || 0, color: '#f44336' }
    ];

    return (
        <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            {loading ? (
                                <Skeleton variant="text" width={200} height={28} />
                            ) : (
                                `💰 ${ t('user.volumeOverview') }`
                            )}
                        </Typography>
                        <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2">
                                    {loading ? (
                                        <Skeleton variant="text" width={120} height={20} />
                                    ) : (
                                        t('user.totalDeposited')
                                    )}
                                </Typography>
                                {loading ? (
                                    <Skeleton variant="rounded" width={80} height={24} />
                                ) : (
                                    <Chip
                                        label={`${ transformToBTC(userData?.totalDepositAmount || 0) }`}
                                        color="success"
                                        size="small"
                                    />
                                )}
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2">
                                    {loading ? (
                                        <Skeleton variant="text" width={120} height={20} />
                                    ) : (
                                        t('user.totalWithdrawn')
                                    )}
                                </Typography>
                                {loading ? (
                                    <Skeleton variant="rounded" width={80} height={24} />
                                ) : (
                                    <Chip
                                        label={`${ transformToBTC(userData?.totalWithdrawAmount || 0) } BTC`}
                                        color="warning"
                                        size="small"
                                    />
                                )}
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" fontWeight="bold">
                                    {loading ? (
                                        <Skeleton variant="text" width={100} height={20} />
                                    ) : (
                                        t('user.netBalance').replace(' (BTC)', '') + ':'
                                    )}
                                </Typography>
                                {loading ? (
                                    <Skeleton variant="rounded" width={80} height={24} />
                                ) : (
                                    <Chip
                                        label={`${ transformToBTC(userData?.netBalance || 0) } BTC`}
                                        color={userData?.netBalance >= 0 ? "success" : "error"}
                                        size="small"
                                    />
                                )}
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            </Grid>
            <Grid item xs={12} md={6}>
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            {loading ? (
                                <Skeleton variant="text" width={250} height={28} />
                            ) : (
                                `📊 ${ t('user.transactionDistribution') }`
                            )}
                        </Typography>
                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                                <Skeleton variant="circular" width={160} height={160} />
                            </Box>
                        ) : (
                            <ResponsiveContainer width="100%" height={200}>
                                <PieChart>
                                    <Pie
                                        data={typeDistribution}
                                        cx="50%"
                                        cy="50%"
                                        outerRadius={80}
                                        fill="#03dac6"
                                        dataKey="value"
                                        label={({ name, percent }) => `${ name } ${ (percent * 100).toFixed(0) }%`}
                                    >
                                        {typeDistribution.map((entry, index) => (
                                            <Cell key={`cell-${ index }`} fill={entry.color} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                </PieChart>
                            </ResponsiveContainer>
                        )}
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    );
};

export default UserVolumeOverview;
